import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import ColorSwatch from "./ColorSwatch";
import AddTagDrawer from "./AddTagDrawer";
import {
  KINDS,
  KIND_LABEL,
  KIND_PLURAL,
  loadTaxonomy,
  updateTaxonomy,
  deleteTaxonomy,
  setIndustryCompliances,
  setComplianceCertifications,
  chipColor,
  usageCount,
} from "./taxonomyApi";
import styles from "./TaxonomyPage.module.css";

/**
 * Taxonomy manager — the three vocabularies plus the relations between them.
 *
 * The relations are the part that is easy to underestimate: they are what
 * makes the public sidebar context-aware. On /industry/banking the Compliance
 * group lists only the compliances mandatory in banking, and that mapping is
 * set here, not derived from which products happen to be tagged. Deriving it
 * would leave the booth pages looking empty until enough products were tagged.
 */
export default function TaxonomyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [kind, setKind] = useState("industry");
  const [drawerKind, setDrawerKind] = useState(null);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [relationFor, setRelationFor] = useState(null); // row being related

  const refresh = useCallback(async (signal) => {
    setError("");
    try {
      const next = await loadTaxonomy(signal);
      setData(next);
      return next;
    } catch (err) {
      if (err?.name !== "AbortError") setError(err?.message || "Could not load the taxonomy.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    refresh(ac.signal);
    return () => ac.abort();
  }, [refresh]);

  const rows = useMemo(() => {
    const all = data?.[kind] || [];
    const q = query.trim().toLowerCase();
    return q ? all.filter((r) => r.name.toLowerCase().includes(q)) : all;
  }, [data, kind, query]);

  /* ── relations ─────────────────────────────────────────────────────── */

  // Which child rows a given parent currently links to.
  const relatedIds = useCallback(
    (parentKind, parentId) => {
      if (parentKind === "industry") {
        return (data?.industry_compliances || [])
          .filter((r) => r.industry_id === parentId)
          .map((r) => r.compliance_id);
      }
      return (data?.compliance_certifications || [])
        .filter((r) => r.compliance_id === parentId)
        .map((r) => r.certification_id);
    },
    [data],
  );

  const childKindOf = (parentKind) =>
    parentKind === "industry" ? "compliance" : "certification";

  const toggleRelation = async (parentKind, parentId, childId) => {
    const current = relatedIds(parentKind, parentId);
    const next = current.includes(childId)
      ? current.filter((id) => id !== childId)
      : [...current, childId];

    setBusyId(`rel-${parentId}`);
    try {
      if (parentKind === "industry") await setIndustryCompliances(parentId, next);
      else await setComplianceCertifications(parentId, next);
      await refresh();
    } catch (err) {
      alert(err?.message || "Could not save the link.");
    } finally {
      setBusyId(null);
    }
  };

  /* ── row actions ───────────────────────────────────────────────────── */

  const saveName = async (row) => {
    const name = draftName.trim();
    setEditingId(null);
    if (!name || name === row.name) return;
    setBusyId(row.id);
    try {
      await updateTaxonomy(kind, row.id, { name });
      await refresh();
    } catch (err) {
      alert(err?.message || "Could not rename.");
    } finally {
      setBusyId(null);
    }
  };

  const saveColor = async (row, color) => {
    setBusyId(row.id);
    try {
      await updateTaxonomy(kind, row.id, { color });
      await refresh();
    } catch (err) {
      alert(err?.message || "Could not change the colour.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    const used = usageCount(row);
    const warning = used
      ? `"${row.name}" is attached to ${used} product${used === 1 ? "" : "s"}.\n\nDeleting it removes those tags from the website and the app. Continue?`
      : `Delete "${row.name}"?`;
    if (!window.confirm(warning)) return;

    setBusyId(row.id);
    try {
      await deleteTaxonomy(kind, row.id, { force: used > 0 });
      await refresh();
    } catch (err) {
      alert(err?.message || "Could not delete.");
    } finally {
      setBusyId(null);
    }
  };

  /* ── render ────────────────────────────────────────────────────────── */

  if (loading) return <div className={styles.state}>Loading taxonomy…</div>;

  if (error) {
    return (
      <div className={styles.state}>
        <p className={styles.error}>{error}</p>
        <Button variant="outlined" onClick={() => refresh()}>
          Retry
        </Button>
      </div>
    );
  }

  const canRelate = kind === "industry" || kind === "compliance";
  const childKind = childKindOf(kind);

  return (
    <div className={styles.wrap}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Taxonomy</h2>
          <p className={styles.subtitle}>
            Industries, compliances and certifications shown as chips on the website and the
            in-app marketplace.
          </p>
        </div>
        <Button variant="contained" onClick={() => setDrawerKind(kind)}>
          + Add {KIND_LABEL[kind].toLowerCase()}
        </Button>
      </header>

      <div className={styles.tabs}>
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={k === kind ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => {
              setKind(k);
              setRelationFor(null);
              setQuery("");
            }}
          >
            {KIND_PLURAL[k]}
            <span className={styles.tabCount}>{(data?.[k] || []).length}</span>
          </button>
        ))}
      </div>

      <input
        className={styles.search}
        placeholder={`Search ${KIND_PLURAL[kind].toLowerCase()}…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {rows.length === 0 ? (
        <div className={styles.state}>
          {query
            ? `No ${KIND_PLURAL[kind].toLowerCase()} match "${query}".`
            : `No ${KIND_PLURAL[kind].toLowerCase()} yet. Add the first one to start tagging products.`}
        </div>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => {
            const open = relationFor === row.id;
            const linked = canRelate ? relatedIds(kind, row.id) : [];

            return (
              <li key={row.id} className={styles.row} data-busy={busyId === row.id || undefined}>
                <div className={styles.rowMain}>
                  <ColorSwatch
                    value={chipColor(row)}
                    onChange={(hex) => saveColor(row, hex)}
                    title={`Colour for ${row.name}`}
                  />

                  {editingId === row.id ? (
                    <input
                      className={styles.rename}
                      autoFocus
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onBlur={() => saveName(row)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveName(row);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.name}
                      title="Rename"
                      onClick={() => {
                        setEditingId(row.id);
                        setDraftName(row.name);
                      }}
                    >
                      {row.name}
                    </button>
                  )}

                  <code className={styles.slug}>/{row.slug}</code>

                  <span className={styles.counts} title="Website listings / in-app SKUs">
                    {row.website_count} web · {row.marketplace_count} app
                  </span>

                  {canRelate && (
                    <button
                      type="button"
                      className={styles.link}
                      onClick={() => setRelationFor(open ? null : row.id)}
                    >
                      {linked.length} {KIND_PLURAL[childKind].toLowerCase()}
                      {open ? " ▲" : " ▼"}
                    </button>
                  )}

                  <button type="button" className={styles.danger} onClick={() => remove(row)}>
                    Delete
                  </button>
                </div>

                {open && (
                  <div className={styles.relations}>
                    <p className={styles.relationsHint}>
                      {kind === "industry"
                        ? `Compliances mandatory in ${row.name}.`
                        : `Certifications ${row.name} requires.`}{" "}
                      Drives the filters on the public {kind} page.
                    </p>

                    {(data?.[childKind] || []).length === 0 ? (
                      <p className={styles.relationsEmpty}>
                        No {KIND_PLURAL[childKind].toLowerCase()} exist yet.
                      </p>
                    ) : (
                      <div className={styles.relationsGrid}>
                        {(data?.[childKind] || []).map((child) => (
                          <label key={child.id} className={styles.check}>
                            <input
                              type="checkbox"
                              checked={linked.includes(child.id)}
                              disabled={busyId === `rel-${row.id}`}
                              onChange={() => toggleRelation(kind, row.id, child.id)}
                            />
                            <span
                              className={styles.chip}
                              style={{
                                color: chipColor(child),
                                background: `${chipColor(child)}1f`,
                              }}
                            >
                              {child.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AddTagDrawer
        open={Boolean(drawerKind)}
        kind={drawerKind}
        onClose={() => setDrawerKind(null)}
        onSaved={() => refresh()}
      />
    </div>
  );
}
